import {
  cloneElement,
  isValidElement,
  MutableRefObject,
  ReactElement,
  SyntheticEvent,
} from 'react';

import {
  ApplyAsChildProps,
  ElementWithRef,
  ElementWithRefProps,
  Handler,
  RenderProp,
} from './popover-helpers.types';

export const INTERNAL_ERRORS = {
  invalidElement: 'applyAsChild: children must be a valid React element',
  invalidArray: 'applyAsChild: children must be a single valid React element',
};

export function applyAsChild<T extends HTMLElement = HTMLElement>({
  asChild = false,
  children,
  propsToInject,
  ref,
}: ApplyAsChildProps<T>): ReactElement | null {
  if (!asChild) return null;

  if (Array.isArray(children)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(INTERNAL_ERRORS.invalidArray, children);
    }
    return null;
  }

  if (!isValidElement(children)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(INTERNAL_ERRORS.invalidElement, children);
    }
    return null;
  }

  return cloneElement(children, {
    ...propsToInject,
    ref,
    style: {
      // Merge existing styles with injected styles
      ...(children.props.style ?? {}),
      ...(propsToInject?.style ?? {}),
    },
  });
}

export function getChildrenRef<T extends HTMLElement = HTMLElement>(
  children: ElementWithRef<T>
) {
  if (isValidElement(children) && 'ref' in children && children.ref != null) {
    return children.ref as MutableRefObject<T>;
  }

  return null;
}

export function isRenderProp<T extends HTMLElement = HTMLElement>(
  children: ElementWithRef<T> | RenderProp<T>
): children is RenderProp<T> {
  if (typeof children !== 'function') return false;

  try {
    // Call the function with dummy props to validate return value
    const result = children({
      open: false,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setOpen: () => {}, // no-op
    });

    return isValidElement(result);
  } catch {
    return false;
  }
}

/**
 * Some basic known events we usually use.
 * If needed we can extend it as the need arise.
 */
const SupportedEventHandlers = ['onClick', 'onFocus', 'onBlur', 'onKeyDown'] as const;

/**
 * Composes multiple event handlers into a single handler.
 * Each handler is called in the order they are provided.
 *
 * @param handlers - The event handlers to compose.
 * @returns A single handler that calls all provided handlers.
 */
function composeHandlers<E = unknown>(
  ...handlers: Array<Handler<E> | undefined>
): Handler<E> {
  return (event: E) => {
    for (const handler of handlers) {
      if (handler) {
        handler(event);
        // Check if propagation was stopped
        const syntheticEvent = event as SyntheticEvent;
        if (syntheticEvent?.isPropagationStopped?.()) {
          break; // Stop calling subsequent handlers
        }
      }
    }
  };
}

/**
 * Merges event handlers. The floating UI event handlers
 * are composed with the provided event handlers from the
 * children, which is used within the PopoverTrigger component.
 *
 * @param base - The base event handlers.
 * @param overrides - The event handlers to override or compose with the base.
 * @returns A new object with merged event handlers.
 */
export function mergeEventHandlers(
  base: Record<string, unknown>,
  overrides: ElementWithRefProps
): Record<string, unknown> {
  const result = { ...base };

  for (const key of SupportedEventHandlers) {
    const baseHandler = base[key] as Handler;
    const overrideHandler = overrides[key] as Handler;

    if (baseHandler || overrideHandler) {
      // running the children handler first so it has the chance to stop
      // propagation to the base handler and trully override its behavior
      result[key] = composeHandlers(overrideHandler, baseHandler);
    }
  }
  return result;
}
